// Combat visualisation: engagement state (3D jitter + step-back) and the
// drawer ticker / modal summaries. Extracted from App.tsx (ROADMAP 7.14); the
// hook order is unchanged.

import { useMemo } from 'react';
import { getCombatFights, getCombatRounds } from '../render/combatRounds.js';
import { summarizeCombatDrawer } from '../render/summarizeCombatDrawer.js';
import { useCombatEngagements } from '../render/useCombatEngagementsHook.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';

export function useCombatView(sim: SimWorld, localPlayerIndex: number) {
  const simEvents = sim.events ?? [];
  const simTick = Number(sim.tick ?? 0);
  const localCapital = useMemo(() => {
    if (!sim.snapshot) return null;
    const globals = sim.snapshot.state.globals as
      | { players?: ReadonlyArray<{ capitalCoord?: { q?: unknown; r?: unknown } | null }> }
      | undefined;
    const coord = globals?.players?.[localPlayerIndex]?.capitalCoord;
    if (!coord || typeof coord.q !== 'number' || typeof coord.r !== 'number') {
      return null;
    }
    return { q: coord.q, r: coord.r };
  }, [sim.snapshot, localPlayerIndex]);

  const engagements = useCombatEngagements(simEvents, simTick);
  const drawerSummary = useMemo(
    () => summarizeCombatDrawer(simEvents, localPlayerIndex, localCapital),
    [simEvents, localCapital, localPlayerIndex],
  );
  const combatFights = useMemo(() => getCombatFights(simEvents), [simEvents]);
  const combatRoundsByFight = useMemo(() => {
    const map = new Map<string, ReadonlyArray<ReturnType<typeof getCombatRounds>[number]>>();
    for (const f of combatFights) {
      const key = `${f.attackerId}::${f.defenderId}`;
      map.set(key, getCombatRounds(simEvents, f.attackerId, f.defenderId));
    }
    return map;
  }, [combatFights, simEvents]);

  return {
    simTick,
    localCapital,
    engagements,
    drawerSummary,
    combatFights,
    combatRoundsByFight,
  };
}
